import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import { Box, Typography, TypographyProps } from "@mui/material";
import ReactQuill from "react-quill";
import { getStyles } from "./styles";
import "./quill.config";
import "react-quill/dist/quill.snow.css";

interface IProps {
    label?: string;
    value?: string;
    placeholder?: string;
    disabled?: boolean;
    name: string;
    onChange: (value: string, field: string) => void;
    maxLength?: number;
    required?: boolean;
    error?: { [key: string]: string };
    typographyProps?: TypographyProps;
}

export const TextEditor: FC<IProps> = ({
    label,
    value,
    disabled,
    placeholder,
    onChange,
    name,
    maxLength,
    required,
    error,
    typographyProps
}) => {
    const quillRef = useRef<ReactQuill | null>(null);
    const [editorValue, setEditorValue] = useState<string>(value || "");

    useEffect(() => {
        setEditorValue(value || "");
    }, [value]);

    const handleChange = (content: string) => {
        const finalContent = content === "<p><br></p>" ? "" : content;

        const plainText = finalContent.replace(/<[^>]+>/g, "");

        if (maxLength && plainText.length > maxLength) {
            quillRef.current?.getEditor().setContents(quillRef.current.getEditor().clipboard.convert(editorValue));
            return;
        }

        setEditorValue(finalContent);
        onChange(finalContent, name);
    };

    const modules = useMemo(() => ({
        toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["clean"]
        ]
    }), []);

    const formats = [
        "header",
        "bold", "italic", "underline",
        "align",
        'list'
    ];

    return (
        <Box sx={getStyles()}>
            {label && (
                <Typography variant="h6" mb={1} {...typographyProps}>
                    {label}
                    {required ? " *" : ""}
                </Typography>
            )}
            <ReactQuill
                ref={quillRef}
                value={editorValue}
                onChange={handleChange}
                readOnly={disabled}
                placeholder={placeholder}
                modules={modules}
                formats={formats}
            />
            {error?.[name] && (
                <Typography minHeight={1} variant="caption" color="red" {...typographyProps}>
                    {error[name]}
                </Typography>
            )}
        </Box>
    );
};